import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ReconciliationService } from "@/api";
import { invalidateMoneyViews } from "./invalidateMoneyViews";
import type {
  reconciliation_CorrectionDTO,
  reconciliation_CorrectionListResponse,
  reconciliation_CreateCorrectionRequest,
  reconciliation_RevertCorrectionRequest,
} from "@/api";

// correctionKeys centralises the TanStack Query namespace for account-direct
// corrections (issue #579). The list is keyed by the debtor account it belongs to.
export const correctionKeys = {
  all: ["reconciliation", "corrections"] as const,
  listByDebtor: (debtorId: string) =>
    [...correctionKeys.all, "by-debtor", debtorId] as const,
};

export function useDebtorCorrections(debtorId: string | null) {
  return useQuery<reconciliation_CorrectionListResponse>({
    queryKey: debtorId
      ? correctionKeys.listByDebtor(debtorId)
      : [...correctionKeys.all, "none"],
    queryFn: () =>
      ReconciliationService.getApiV1ReconciliationDebtorAccountsCorrections(
        debtorId as string,
      ),
    enabled: !!debtorId,
  });
}

// Corrections move the account's money, so they refresh the money views. The
// shared helper also cancels in-flight reads first, which is what stops a
// read issued before the posting from landing afterwards and repainting the
// pre-correction numbers. See invalidateMoneyViews.
async function invalidateCorrections(
  queryClient: ReturnType<typeof useQueryClient>,
) {
  await invalidateMoneyViews(queryClient);
}

export interface CreateCorrectionInput {
  debtorAccountId: string;
  body: reconciliation_CreateCorrectionRequest;
}

export function useCreateCorrection() {
  const queryClient = useQueryClient();
  return useMutation<
    reconciliation_CorrectionDTO,
    unknown,
    CreateCorrectionInput
  >({
    mutationFn: ({ debtorAccountId, body }) =>
      ReconciliationService.postApiV1ReconciliationDebtorAccountsCorrections(
        debtorAccountId,
        body,
      ),
    onSuccess: () => invalidateCorrections(queryClient),
  });
}

export interface RevertCorrectionInput {
  correctionId: string;
  body?: reconciliation_RevertCorrectionRequest;
}

export function useRevertCorrection() {
  const queryClient = useQueryClient();
  return useMutation<
    reconciliation_CorrectionDTO,
    unknown,
    RevertCorrectionInput
  >({
    mutationFn: ({ correctionId, body }) =>
      ReconciliationService.postApiV1ReconciliationCorrectionsRevert(
        correctionId,
        body,
      ),
    onSuccess: () => invalidateCorrections(queryClient),
  });
}
